import React, { useEffect, useMemo, useState } from 'react';
import { Section_Title } from 'monica-alexandria';
import styled from 'styled-components';
import Timeline_ from '../../../../components/timeline/Timeline_';
import { read_dolores_seller_user, read_dolores_users } from '../../../../services/api/HumanResources';
import { get_brand_timeline_page } from '../../../../services/api/Prospects';
import { useTimelinePages } from '../../../../utils/timeline/use_timeline_pages';

const LeadsCombinedTimeline = styled.section`
    display: grid;
    gap: 1.6rem;
    min-width: 0;
    padding-top: 2rem;
    border-top: 0.1rem solid ${p => p.theme.low};
`;

const read_text = (value) => `${value || ''}`.trim();

/**
 * Reads the saved Brands that can own timeline pages in the Lead slider.
 */
export const read_combined_timeline_brands = (selectedLead = {}, t = value => value) => {
    return (selectedLead?.brands || []).map((brand, index) => {
        const id = read_text(brand?._id || brand?.brandId);

        return {
            id,
            name: read_text(brand?.internalName || brand?.name || brand?.brand) || `${t('Brand')} ${index + 1}`,
            updatedAt: brand?.updatedAt || '',
            archived: brand?.archived === true
        };
    }).filter((brand) => brand.id && !brand.archived);
};

/**
 * Keeps timeline event identities unique across Brands and carries visible Brand context.
 */
export const read_brand_scoped_timeline_events = (events = [], brand = {}) => {
    return events.map((event, index) => {
        const originalIdentity = read_text(event?.id || event?.eventId || event?.sourceKey || event?._id) || `event-${index}`;
        const scopedIdentity = `${brand.id}:${originalIdentity}`;
        const timelineBrand = { id: brand.id, name: brand.name };

        return {
            ...(event || {}),
            id: scopedIdentity,
            eventId: scopedIdentity,
            sourceKey: scopedIdentity,
            timelineBrand,
            detail: {
                ...(event?.detail || {}),
                timelineBrand
            },
            data: {
                ...(event?.data || {}),
                timelineBrand
            }
        };
    });
};

const read_cursor_map = (cursor, brands) => {
    if (!cursor) {
        return Object.fromEntries(brands.map((brand) => [brand.id, null]));
    }

    try {
        const parsed = JSON.parse(cursor);

        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const read_publication_state = (pages = []) => {
    const states = pages.map((page) => read_text(page?.publication?.state || page?.publication?.status).toLowerCase());

    if (states.includes('failed')) {
        return { state: 'failed' };
    }

    if (states.includes('pending')) {
        return { state: 'pending' };
    }

    return { state: 'synced' };
};

/**
 * Loads one cursor page per unfinished Brand and returns one merged timeline page.
 */
export const read_combined_timeline_page = async (prospectId, brands, cursor, readPage = get_brand_timeline_page) => {
    const cursorMap = read_cursor_map(cursor, brands);
    const requestedBrands = brands.filter((brand) => Object.prototype.hasOwnProperty.call(cursorMap, brand.id));
    const results = await Promise.all(requestedBrands.map(async (brand) => {
        const page = await readPage(prospectId, brand.id, cursorMap[brand.id] || null);

        return { brand, page: page || {} };
    }));
    const nextCursorMap = Object.fromEntries(results.filter(({ page }) => page?.hasMore && page?.nextCursor)
        .map(({ brand, page }) => [brand.id, page.nextCursor]));
    const warnings = [...new Set(results.map(({ page }) => read_text(page?.warning)).filter(Boolean))];

    return {
        events: results.flatMap(({ brand, page }) => read_brand_scoped_timeline_events(page?.events || [], brand)),
        hasMore: Object.keys(nextCursorMap).length > 0,
        nextCursor: Object.keys(nextCursorMap).length ? JSON.stringify(nextCursorMap) : '',
        warning: warnings.join(' '),
        publication: read_publication_state(results.map(({ page }) => page))
    };
};

/**
 * Displays every saved Brand timeline in one chronological Lead-level stream.
 */
export default function Leads_Combined_Timeline(props) {
    const [teamMembers, setTeamMembers] = useState([]);
    const brands = useMemo(() => read_combined_timeline_brands(props?.selectedLead, props?.t), [props?.selectedLead?.brands, props?.t]);
    const prospectId = read_text(props?.selectedLead?._id);
    const identity = [prospectId, ...brands.map((brand) => brand.id)].filter(Boolean).join(':');
    const refreshKey = brands.map((brand) => `${brand.id}:${brand.updatedAt}`).join('|');
    const page = useTimelinePages({
        identity,
        refreshKey,
        t: props?.t,
        readPage: (cursor) => read_combined_timeline_page(prospectId, brands, cursor)
    });

    useEffect(() => {
        let active = true;

        read_dolores_users()
            .then((users) => {
                if (active) {
                    setTeamMembers((users || []).map(read_dolores_seller_user));
                }
            })
            .catch(() => {});

        return () => {
            active = false;
        };
    }, []);

    return (
        <LeadsCombinedTimeline data-testid="lead-combined-timeline">
            <Section_Title text={props?.t('Combined timeline')} />
            <Timeline_
                {...page}
                showBrandLabels={brands.length > 1}
                teamMembers={teamMembers}
                t={props?.t}
                onLoadMore={page.load_more}
                onRetry={page.retry}
                onRefresh={page.refresh}
            />
        </LeadsCombinedTimeline>
    );
}

export { Leads_Combined_Timeline };
